import { PatientService } from 'src/app/core/services/patient.service';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay, take, switchMap } from 'rxjs/operators';
import { Patient } from 'src/app/core/models/patient.model';
import { keys } from 'lodash';
import { PatientInfoValueLabel } from 'src/app/core/dictionaries/patient-info-value-label.constant';
import { PatientInfoValueIcon } from 'src/app/core/dictionaries/patient-info-value-icon.constant';
import { PatientInfoValueType } from 'src/app/core/dictionaries/patient-info-value-type.constant';
import { PatientInfoValueGroup } from 'src/app/core/dictionaries/patient-info-value-group.constant';
import { PatientInfoValueOrder } from 'src/app/core/dictionaries/patient-info-value-order.constant';
import { PatientInfoGroupLabel } from 'src/app/core/dictionaries/patient-info-group-label.constant';
import { PatientInfoGroupOrder } from 'src/app/core/dictionaries/patient-info-group-order.constant';
import { PatientInfoValues } from 'src/app/core/enums/patient-info-values.enum';
import { GenderLabel } from 'src/app/core/dictionaries/gender-label.constant';
import { PatientDialogService } from 'src/app/core/services/patient-dialog.service';

@Component({
  selector: 'app-patient-info',
  templateUrl: './patient-info.component.html',
  styleUrls: ['./patient-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PatientInfoComponent implements OnInit {

  patient$ = this.patientService.patient$;
  patientInfo$ = this.getPatientInfo();

  constructor(
    private patientService: PatientService,
    private patientDialogService: PatientDialogService
  ) { }

  ngOnInit(): void {
  }

  editPatient() {
    this.patient$.pipe(
      take(1),
      switchMap(patient => this.patientDialogService.openPatientDialog(patient))
    ).subscribe();
  }

  getPatientInfo(): Observable<any> {
    return this.patient$.pipe(
      map(patient => {
        if (patient) {
          return this.mapPatientToPatientInfo(patient);
        }
        return [];
      }),
      shareReplay(1)
    );
  }

  private mapPatientToPatientInfo(patient: Patient) {
    // const patientInfoValues = keys(PatientInfoValueLabel).filter(key => patient[key]);
    const patientInfoValues = keys(PatientInfoValueLabel);
    return patientInfoValues.reduce((accumulator, currentValue) => {
      const currentValueGroup = PatientInfoValueGroup[currentValue];
      const currentValueOrder = PatientInfoValueOrder[currentValue];
      const groupLabel = PatientInfoGroupLabel[currentValueGroup];
      const groupOrder = PatientInfoGroupOrder[currentValueGroup];
      const groupIndex = accumulator.findIndex(value => value.label === groupLabel);
      if (groupIndex >= 0) {
        accumulator[groupIndex].value.splice(currentValueOrder, 0, this.getValueLabelFromValue(patient, currentValue));
      } else {
        accumulator.splice(groupOrder, 0, this.getGroupFromValue(patient, currentValue));
      }
      return accumulator;
    }, []);
  }

  private getValueLabelFromValue(patient: Patient, value: string) {
    return {
      label: PatientInfoValueLabel[value],
      value: this.getFormattedValue(patient, value),
      icon: PatientInfoValueIcon[value],
      type: PatientInfoValueType[value] || null
    };
  }

  private getFormattedValue(patient: Patient, value: string) {
    if (!patient[value]) {
      return null;
    }

    if (value === PatientInfoValues.BIRTH_DATE) {
      return this.patientService.getAgeFromBirthDate(patient[value]);
    }

    if (value === PatientInfoValues.WEIGHT) {
      return `${patient[value]} kg`;
    }

    if (value === PatientInfoValues.GENDER) {
      return GenderLabel[patient[value]];
    }

    return patient[value];
  }

  private getGroupFromValue(patient: Patient, value: string) {
    return {
      label: PatientInfoGroupLabel[PatientInfoValueGroup[value]],
      value: [this.getValueLabelFromValue(patient, value)]
    };
  }

}
